// scheduling/components/reports/UnscheduledHoursReport.jsx
// Potentially Unscheduled Hours (Trello #252 checklist / #387).
//
// The port of the legacy Empire "Return Workload & Coverage Gap Analyzer", which emailed a
// CSV to taxdepartment@bmss.com until its upstream caller was disabled on 2026-07-02.
//
//   Potential unscheduled = max(0, PY prep WIP − CY prep WIP − scheduled)
//
// WHY THIS IS A PAGE AND NOT A REVIVED EMAIL: every row is actionable. Clicking Schedule
// opens the same staff chooser the My Jobs dashboard uses, so a JM can go from "this return
// has 40 unscheduled prep hours" to hours on the calendar without leaving the finding
// behind. The email could only ever tell you.
//
// ─── TOOLTIP RULE ───────────────────────────────────────────────────────────
// Every `title` here is written for a tax manager, not a developer: no table names, no
// column names, no function names, no history of what the legacy report got wrong. The
// technical reasoning lives in these comments and in api/rprsReports/unscheduled.js, where
// the people who need it will actually look. A tooltip that says
// "rprs.JobsHistory.EffectiveCompletedDate" tells the reader nothing they can use.
//
// ─── THE ARITHMETIC IS LABELLED, DELIBERATELY ───────────────────────────────
// Three prior-year columns are shown (budget, prep, all) because they answer different
// questions, but only ONE drives the gap: prior-year prep. Anyone reading a variance report
// will assume the biggest number is the one being subtracted, so the header states the
// formula outright rather than leaving it to be inferred.
//
// ─── TWO SECTIONS, BECAUSE TWO DIFFERENT CLAIMS ─────────────────────────────
// A job with no prior-year job cannot have a prior-year baseline, and 438 of ~1,790 are in
// that position — new clients, new entities, first-year returns. The legacy query gave them
// PY = 0, so max(0, 0 − CY − scheduled) was always 0 and they read as "fully scheduled" no
// matter how empty their calendar was. That is a false negative, not a zero.
//
// So they get their own section and, where the job carries a current-year prep BUDGET, a gap
// computed from that instead. Where there is neither, the gap is BLANK — never 0. "We cannot
// compute this" and "there is nothing to schedule" must not look alike.
//
// ─── PHASE COMES FROM getJobPhase(), NOT FROM THE SERVER ────────────────────
// The handler returns the raw status string and this computes the milestone with
// schedConstants' getJobPhase — the app's canonical 178-status mapping, shared with JobsView
// and IDDReview so a job cannot show one milestone here and another there. There are already
// three divergent copies of that mapping in the codebase; pushing it into SQL to filter
// server-side would have made a fourth. Filtering here costs nothing: the payload is ~1,600
// rows firm-wide and a couple of dozen for a typical JM.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchUnscheduledReport } from '../../services/reportsApi';
import { fetchStaff } from '../../services/schedData';
import { useMe } from '../../hooks/useMe';
import { getJobPhase, PHASE_LABELS, jobDept, BRAND } from '../../utils/schedConstants';
import { useSort, SortableTh, PlainTh } from './SortableTh';
import { exportCsv } from '../../utils/csv';
import ScheduleChooserModal from '../ScheduleChooserModal';

// Finalization. The legacy forced the gap to 0 for these on the assumption that a return in
// assembly/e-file needs no more prep — usually true, which is why they are hidden by
// default. But "usually" is not "always": a mis-statused job sitting in Finalization with 40
// unscheduled hours is exactly the anomaly worth catching, so the real number is kept and
// the row is merely de-emphasised when shown.
const PHASE_FINALIZATION = 4;

// Dates arrive as YYYY-MM-DD strings and render verbatim. No Date parsing: the server stores
// UTC and BMSS is Central, so new Date('2026-10-15') would parse as UTC and display a day
// early on a date-only value.
const fmtDate = (iso) => iso || '—';
// Blank, not 0 — a missing baseline is not a zero gap. See the header.
const fmtNum = (n) => (n == null ? '—' : n);
const fmtHrs = (n) => (n == null ? '—' : (n === 0 ? '0' : n));

const SORT_SPECS = {
  gap: { get: (r) => r.gap, dir: 'desc' },
  client: { get: (r) => r.clientCode, dir: 'asc' },
  clientName: { get: (r) => r.clientName, dir: 'asc' },
  office: { get: (r) => r.office, dir: 'asc' },
  job: { get: (r) => r.jobName, dir: 'asc' },
  jm: { get: (r) => r.jobManager, dir: 'asc' },
  jp: { get: (r) => r.jobPartner, dir: 'asc' },
  due: { get: (r) => r.currentDueDate, dir: 'asc' },
  idd: { get: (r) => r.idd, dir: 'asc' },
  status: { get: (r) => r.statusDesc, dir: 'asc' },
  phase: { get: (r) => r.phase, dir: 'asc' },
  pyDone: { get: (r) => r.pyCompletion, dir: 'asc' },
  pyBudget: { get: (r) => r.pyPrepBudget, dir: 'desc' },
  pyPrep: { get: (r) => r.pyPrepWip, dir: 'desc' },
  pyTotal: { get: (r) => r.pyTotalWip, dir: 'desc' },
  cyPrep: { get: (r) => r.cyPrepWip, dir: 'desc' },
  sched: { get: (r) => r.scheduled, dir: 'desc' },
};

const CSV_COLUMNS = [
  { label: 'Client code', get: (r) => r.clientCode },
  { label: 'Client', get: (r) => r.clientName },
  { label: 'Office', get: (r) => r.office },
  { label: 'Job', get: (r) => r.jobName },
  { label: 'Service', get: (r) => r.serviceDesc },
  { label: 'Client partner', get: (r) => r.clientPartner },
  { label: 'Client manager', get: (r) => r.clientManager },
  { label: 'Job partner', get: (r) => r.jobPartner },
  { label: 'Job manager', get: (r) => r.jobManager },
  { label: 'Prior-year job partner', get: (r) => r.pyJobPartner },
  { label: 'Prior-year job manager', get: (r) => r.pyJobManager },
  { label: 'Lead changed since last year', get: (r) => (r.pyJmDiffers || r.pyJpDiffers ? 'yes' : '') },
  { label: 'Due date', get: (r) => r.currentDueDate },
  { label: 'Internal due date', get: (r) => r.idd },
  { label: 'Status', get: (r) => r.statusDesc },
  { label: 'Milestone', get: (r) => r.phaseLabel },
  { label: 'Prior-year completed', get: (r) => r.pyCompletion },
  { label: 'Prior-year prep budget', get: (r) => r.pyPrepBudget },
  { label: 'Prior-year prep hours', get: (r) => r.pyPrepWip },
  { label: 'Prior-year total hours', get: (r) => r.pyTotalWip },
  { label: 'This year prep hours', get: (r) => r.cyPrepWip },
  { label: 'This year total hours', get: (r) => r.cyTotalWip },
  { label: 'Scheduled from today', get: (r) => r.scheduled },
  { label: 'Baseline used', get: (r) => r.baseline },
  { label: 'Baseline type', get: (r) => (r.baselineSource === 'cy_prep_budget' ? 'this-year prep budget'
    : r.baselineSource === 'py_prep_wip' ? 'prior-year prep hours' : '') },
  { label: 'Potentially unscheduled', get: (r) => r.gap },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// One table, rendered twice — once per section. Kept as a component rather than duplicated
// so a column added to the main table cannot silently miss the no-baseline one.
function GapTable({ rows, sort, showBaselineSource, onPick, emptyText }) {
  return (
    <div className="sched-mgr-table-wrap sched-rpt-scroll">
      <table className="sched-mgr-table sched-rpt-table">
        <thead>
          <tr className="sched-rpt-colrow">
            <SortableTh sort={sort} sortKey="client" label="Code" title="Client code." />
            <SortableTh sort={sort} sortKey="clientName" label="Client" title="Client name." />
            <SortableTh sort={sort} sortKey="office" label="Off" title="The client's office." />
            <SortableTh sort={sort} sortKey="job" label="Job" title="The job as it is named in Practice Engine." />
            <SortableTh sort={sort} sortKey="jm" label="JM"
              title="Job manager. A warning triangle means someone else managed this return last year — worth a word with them before you schedule, since they know what it actually took." />
            <SortableTh sort={sort} sortKey="jp" label="JP" title="Job partner." />
            <SortableTh sort={sort} sortKey="due" label="Due"
              title="The return's current due date, including any extension." />
            <SortableTh sort={sort} sortKey="idd" label="IDD" title="Internal due date." />
            <SortableTh sort={sort} sortKey="status" label="Status" title="The job's current work status." />
            <SortableTh sort={sort} sortKey="phase" label="Milestone"
              title="Where the job sits in the workflow: Not Started, Prep, Review, Member Review or Finalization. Matches the milestone shown on the My Jobs dashboard." />
            <SortableTh sort={sort} sortKey="pyDone" label="PY done"
              title="When last year's return was actually finished." />
            <SortableTh sort={sort} sortKey="pyBudget" label="PY bud" className="sched-mgr-th-right"
              title="Hours budgeted for prep on last year's return. Shown for context - it is not part of the calculation." />
            <SortableTh sort={sort} sortKey="pyPrep" label="PY prep" className="sched-mgr-th-right"
              title="Prep hours actually charged to last year's return. This is the starting point for the calculation." />
            <SortableTh sort={sort} sortKey="pyTotal" label="PY all" className="sched-mgr-th-right"
              title="All hours charged to last year's return, every task. Shown for context - review and admin time is not prep, so it is not part of the calculation." />
            <SortableTh sort={sort} sortKey="cyPrep" label="CY prep" className="sched-mgr-th-right"
              title="Prep hours charged to this year's return so far. Subtracted from last year's prep." />
            <SortableTh sort={sort} sortKey="sched" label="Sched" className="sched-mgr-th-right"
              title="Hours already on the calendar from today onward, added up across everyone scheduled on the job." />
            {showBaselineSource && (
              <PlainTh label="Based on"
                title="Which figure the estimate started from. Rows reading 'CY budget' have no prior year, so this year's prep budget is used instead." />
            )}
            <SortableTh sort={sort} sortKey="gap" label="Unscheduled" className="sched-mgr-th-right"
              title="Last year's prep, less what has been charged this year, less what is already scheduled. Blank means there is nothing to compare against - which is not the same as nothing to schedule." />
            <PlainTh label="" title="Pick staff and split these hours." />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isFinal = r.phase === PHASE_FINALIZATION;
            return (
              <tr key={r.jobId} style={{ opacity: isFinal ? 0.55 : 1 }}
                title={isFinal
                  ? `This job is in Finalization ("${r.statusDesc}"), so it most likely needs no further prep time. The hours are still shown in case the status is out of date.`
                  : undefined}>
                <td>{r.clientCode}</td>
                <td>{r.clientName}</td>
                <td className="sched-rpt-muted-sm">{r.office || '—'}</td>
                <td>{r.jobName}</td>
                <td className="sched-rpt-muted-sm">
                  {r.jobManager || '—'}
                  {(r.pyJmDiffers || r.pyJpDiffers) && (
                    <span title={`Last year this return was managed by ${r.pyJobManager || '—'} with ${r.pyJobPartner || '—'} as partner.`}> &#9888;</span>
                  )}
                </td>
                <td className="sched-rpt-muted-sm">{r.jobPartner || '—'}</td>
                <td className="sched-rpt-muted-sm">{fmtDate(r.currentDueDate)}</td>
                <td className="sched-rpt-muted-sm">{fmtDate(r.idd)}</td>
                <td className="sched-rpt-muted-sm">{r.statusDesc}</td>
                <td className="sched-rpt-muted-sm">{r.phaseLabel}</td>
                <td className="sched-rpt-muted-sm">{fmtDate(r.pyCompletion)}</td>
                <td className="sched-mgr-td-right sched-rpt-muted-sm">{fmtHrs(r.pyPrepBudget)}</td>
                <td className="sched-mgr-td-right">{fmtHrs(r.pyPrepWip)}</td>
                <td className="sched-mgr-td-right sched-rpt-muted-sm">{fmtHrs(r.pyTotalWip)}</td>
                <td className="sched-mgr-td-right sched-rpt-muted-sm">{fmtHrs(r.cyPrepWip)}</td>
                <td className="sched-mgr-td-right sched-rpt-muted-sm">{fmtHrs(r.scheduled)}</td>
                {showBaselineSource && (
                  <td className="sched-rpt-muted-sm">
                    {r.baselineSource === 'cy_prep_budget' ? 'CY budget'
                      : r.baselineSource === 'py_prep_wip' ? 'PY prep' : '—'}
                  </td>
                )}
                <td className="sched-mgr-td-right sched-rpt-pct">{fmtNum(r.gap)}</td>
                <td>
                  {/* The row itself is no longer clickable. With a real button here, a
                      whole-row handler made every cell a hair-trigger for a modal — and
                      selecting a client name to copy it fired the modal too. */}
                  <button className="sched-assign-btn sched-assign-btn--sm"
                    onClick={() => onPick(r)}
                    title={`Pick staff and split these hours across them, for ${r.clientName} - ${r.jobName}.`}>
                    Schedule
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={showBaselineSource ? 19 : 18} className="sched-rpt-muted">{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function UnscheduledHoursReport() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [yearOverride, setYearOverride] = useState('');    // '' = let the server auto-flip
  // Free text, not a picklist: a threshold is a number the user has in mind, and every
  // fixed set of options is wrong for somebody. Empty is the default and means "any gap",
  // shown as a greyed 0 placeholder rather than a literal 0 the user has to clear.
  const [minGapText, setMinGapText] = useState('');
  const [includeFinal, setIncludeFinal] = useState(false);

  // Only needed by the chooser (manual staff search + dept/office lookups). Fetched here
  // rather than via useSchedulerData, which fires the whole grid bootstrap — ~11MB, of
  // which the jobs read alone is 9MB. A report has no business paying that.
  const [staff, setStaff] = useState([]);
  const { meStaffId, meName } = useMe(staff);

  // { job, initial } — `initial` is non-null only when restored from a suggestions
  // round-trip, and carries the picks made over there.
  const [chooser, setChooser] = useState(null);

  const sortMain = useSort(SORT_SPECS, 'gap', 'usort');
  const sortNoBase = useSort(SORT_SPECS, 'gap', 'nsort');

  const minGap = Number(minGapText) > 0 ? Number(minGapText) : 0;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUnscheduledReport({
      returnYear: yearOverride === '' ? undefined : Number(yearOverride),
      today: todayISO(),
    })
      .then((r) => { if (!cancelled) { setData(r); setError(null); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [yearOverride]);

  useEffect(() => {
    let cancelled = false;
    fetchStaff()
      .then((s) => { if (!cancelled) setStaff(Array.isArray(s) ? s : []); })
      // Non-fatal: the report is still readable, only the chooser needs staff.
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  // ── Reopen the chooser after a "View detailed staff suggestions" round-trip ──
  //
  // The chooser stashes pendingChooserDraft and navigates to the suggestions page; that
  // page writes the picks back into the same key and returns here (its chooserReturnUrl
  // now has a branch for origin='report'). Mirrors Dashboard.jsx's restore effect.
  //
  // The draft carries the whole job (name, client, remaining hours, dept), so this does NOT
  // have to wait for the report rows to load — which matters, because the user may land
  // back here before the query finishes.
  //
  // The origin check is what keeps the two restore points from stealing each other's work:
  // a draft raised from the My Jobs sidebar must be consumed by the dashboard, not by
  // whichever surface mounts first.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !meStaffId) return;
    const raw = sessionStorage.getItem('pendingChooserDraft');
    if (!raw) return;
    let d;
    try { d = JSON.parse(raw); } catch { return; }
    if (d.origin !== 'report') return;          // not ours — leave it for the dashboard
    restoredRef.current = true;
    sessionStorage.removeItem('pendingChooserDraft');
    const picks = Array.isArray(d.pickedStaffIds) ? d.pickedStaffIds : [];
    const merged = [...new Set([...(d.selected || []), ...picks])];
    setChooser({
      job: {
        JobId: d.jobId, Name: d.jobName, ClientName: d.clientName,
        remaining: d.remaining, wipTotal: d.wipTotal || 0, dept: d.dept || null,
        Deadline: d.Deadline || null,
      },
      initial: {
        selected: merged.length ? merged : undefined,
        showOthers: true,
        // Names/levels for picks the chooser would not otherwise be able to label.
        extraStaff: d.pickedStaffMeta || [],
        placeholders: d.placeholders || [],
        manualPicks: d.manualPicks || [],
        window: d.window || null,
        // `allocations` is deliberately NOT restored, matching Dashboard.jsx. The point of
        // the round-trip is usually to ADD staff, and the chooser only falls back to an
        // even split when allocations are absent — carry the old ones over and the people
        // just picked would arrive with no hours while the originals kept theirs.
      },
    });
  }, [meStaffId]);

  // Milestone is derived once, here, and carried on the row so sorting and CSV agree with
  // what is rendered.
  const enriched = useMemo(() => (data?.rows || []).map((r) => {
    const phase = getJobPhase(r.statusDesc);
    return { ...r, phase, phaseLabel: phase >= 0 ? PHASE_LABELS[phase] : '—' };
  }), [data]);

  const visible = useMemo(() => enriched.filter((r) => {
    if (!includeFinal && r.phase === PHASE_FINALIZATION) return false;
    // A blank gap has no baseline: it can never satisfy a threshold, but it must not be
    // filtered away either — those rows are the no-baseline section's entire content.
    if (r.gap == null) return true;
    return r.gap > minGap;
  }), [enriched, includeFinal, minGap]);

  // The split is on whether a PRIOR-YEAR baseline exists, not on whether a gap does.
  const mainRows = useMemo(
    () => sortMain.sortRows(visible.filter((r) => r.baselineSource === 'py_prep_wip')),
    [visible, sortMain],
  );
  const noBaseRows = useMemo(
    () => sortNoBase.sortRows(visible.filter((r) => r.baselineSource !== 'py_prep_wip')),
    [visible, sortNoBase],
  );

  const openChooser = useCallback((r) => {
    if (!r.jobId) return;
    setChooser({
      job: {
        JobId: r.jobId,
        Name: r.jobName,
        ClientName: r.clientName,
        // The gap IS the remaining work to place, which is the whole point of the report.
        remaining: Math.max(0, r.gap || 0),
        wipTotal: r.cyTotalWip,
        dept: jobDept(r.serviceDesc),
        Deadline: r.idd || null,
      },
      initial: null,
    });
  }, []);

  const mainHours = useMemo(
    () => Math.round(mainRows.reduce((a, r) => a + (r.gap || 0), 0) * 100) / 100,
    [mainRows],
  );

  // ── Headline figures MUST describe the rows on screen ───────────────────────
  //
  // These were originally the server's summary, computed over the whole scope before any
  // client filtering — so the stat row said 1,602 jobs while the two tables added up to
  // 1,106, and there was no way to reconcile the two from the page. The 496 difference was
  // 157 jobs hidden by the Finalization toggle plus 339 whose gap is exactly 0.
  //
  // A headline that counts rows the reader cannot see is worse than no headline: it invites
  // exactly the "these numbers don't add up" question, and the honest answer is a paragraph.
  // So everything here is derived from the SAME filtered set the tables render, and what was
  // excluded is stated separately rather than silently folded in.
  const stats = useMemo(() => {
    const shown = [...mainRows, ...noBaseRows];
    const withGap = shown.filter(r => (r.gap || 0) > 0);
    return {
      jobs: shown.length,
      withGap: withGap.length,
      gapHours: Math.round(withGap.reduce((a, r) => a + (r.gap || 0), 0) * 100) / 100,
      scheduledHours: Math.round(shown.reduce((a, r) => a + r.scheduled, 0) * 100) / 100,
      // Section-level counts, also over the visible rows — the prose under the second table
      // quotes these, and it described the whole scope before for the same reason.
      fallbackBaseline: noBaseRows.filter(r => r.baselineSource === 'cy_prep_budget').length,
      noBaseline: noBaseRows.filter(r => r.baselineSource === null).length,
    };
  }, [mainRows, noBaseRows]);

  // What the filters are holding back, so the scope is still visible without polluting the
  // figures above. Counted off `enriched` (everything the server returned) minus what is shown.
  const excluded = useMemo(() => {
    const finalization = enriched.filter(r => r.phase === PHASE_FINALIZATION).length;
    const fullyScheduled = enriched.filter(
      r => r.phase !== PHASE_FINALIZATION && r.gap != null && r.gap <= minGap,
    ).length;
    return { total: enriched.length, finalization, fullyScheduled };
  }, [enriched, minGap]);

  return (
    <div className="sched-rpt-root">
      <div className="sched-rpt-builder">
        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel"
              title="Which return year to show. The default follows the same rule the old weekly email used: from January to October it reports on last year's returns, the ones being worked now; in November and December it switches to the coming year.">
              Return year
            </label>
            <select className="sched-rpt-bsel" value={yearOverride}
              onChange={(e) => setYearOverride(e.target.value)}>
              <option value="">{data ? `Auto — ${data.autoReturnYear}` : 'Auto'}</option>
              {(data?.yearOptions || []).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </span>

          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel"
              title="Hide jobs with fewer potentially-unscheduled hours than this. Leave it empty to see every job with any hours outstanding. Jobs with nothing to compare against are always listed, since they have no number to measure.">
              Min unscheduled
            </label>
            <input className="sched-rpt-btext sched-rpt-bnum" type="number" min="0" step="1"
              placeholder="0" value={minGapText}
              onChange={(e) => setMinGapText(e.target.value)}
              aria-label="Minimum potentially unscheduled hours" />
          </span>

          <label className="sched-rpt-check"
            title="Jobs that have reached Finalization - assembly, checking, e-filing. Hidden by default because they almost never need more prep time. When shown they are greyed, and their hours are real rather than zeroed, so a job left on the wrong status is still visible.">
            <input type="checkbox" checked={includeFinal}
              onChange={(e) => setIncludeFinal(e.target.checked)} />
            {' '}Include Finalization
          </label>

          {(mainRows.length > 0 || noBaseRows.length > 0) && (
            <button className="sched-rpt-csv" style={{ marginLeft: 'auto' }}
              onClick={() => exportCsv('rprs-unscheduled-hours', [...mainRows, ...noBaseRows], CSV_COLUMNS)}
              title="Download the rows below as a spreadsheet. Replaces the attachment the old weekly email sent.">
              CSV
            </button>
          )}
        </div>
      </div>

      {error && <div className="sched-rpt-error">Couldn&rsquo;t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading&hellip;</div>}

      {!loading && !error && data && (
        <>
          <div className="sched-rpt-summary">
            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l"
                title="Prep hours last year's return needed that have neither been charged nor scheduled this year.">
                Potentially unscheduled &mdash; {data.returnYear} returns
              </div>
              <div className="sched-rpt-statrow">
                <div className="sched-rpt-stat"
                  title="Jobs listed below. This is the two tables added together, so it always matches what you can see.">
                  <div className="sched-rpt-stat-n">{stats.jobs}</div>
                  <div className="sched-rpt-stat-l">Jobs</div>
                </div>
                <div className="sched-rpt-stat" title="Of those, the ones with hours outstanding.">
                  <div className="sched-rpt-stat-n">{stats.withGap}</div>
                  <div className="sched-rpt-stat-l">With a gap</div>
                </div>
                <div className="sched-rpt-stat" title="Total potentially-unscheduled hours across the jobs listed.">
                  <div className="sched-rpt-stat-n">{stats.gapHours}</div>
                  <div className="sched-rpt-stat-l">Hours</div>
                </div>
                <div className="sched-rpt-stat"
                  title="Hours already on the calendar from today onward, for the jobs listed.">
                  <div className="sched-rpt-stat-n">{stats.scheduledHours}</div>
                  <div className="sched-rpt-stat-l">Scheduled</div>
                </div>
              </div>
              {/* What the filters are holding back. Without this the figures above look like
                  the whole picture, and the scope silently disappears. */}
              {(excluded.finalization > 0 || excluded.fullyScheduled > 0) && (
                <div className="sched-rpt-muted-sm" style={{ marginTop: 6 }}
                  title="These jobs are in scope but not listed, because of the filters above.">
                  {excluded.total} return job{excluded.total === 1 ? '' : 's'} in scope
                  {excluded.fullyScheduled > 0 && <> &middot; {excluded.fullyScheduled} with
                    {minGap > 0 ? ` ${minGap} h or less` : ' nothing'} outstanding</>}
                  {excluded.finalization > 0 && !includeFinal &&
                    <> &middot; {excluded.finalization} in Finalization, hidden</>}
                </div>
              )}
            </div>

            <div className="sched-rpt-legend">
              <div className="sched-rpt-muted-sm">
                <strong>Last year&rsquo;s prep hours, less prep charged this year, less what is
                already scheduled.</strong>{' '}
                Only <em>prep</em> time counts. The prior-year budget and total columns are there
                for context and are <em>not</em> subtracted.
              </div>
              <div className="sched-rpt-muted-sm">
                {data.scope === 'firm'
                  ? <>Showing the <strong>whole firm</strong>.</>
                  : <>Showing only jobs where <strong>you are job manager or job partner</strong>.</>}
                {' '}Scheduled hours count from today onward, across everyone on the job.
              </div>
              <div className="sched-rpt-muted-sm">
                Use <strong>Schedule</strong> on any row to pick staff and split the hours &mdash;
                the same chooser the My Jobs dashboard uses.
              </div>
              {!data.isAutoYear && (
                <div className="sched-rpt-muted-sm">
                  <strong>Year overridden</strong> to {data.returnYear}; the automatic choice for
                  today is {data.autoReturnYear}.
                </div>
              )}
            </div>
          </div>

          {/* Nothing at all in scope. Distinct from "everything is scheduled", and the
              distinction matters: the report is visible to anyone who leads ANY job, which on
              prod is 149 staff, while only ~98 lead a TAX RETURN. So a CAS or audit lead
              legitimately lands here with zero rows, and must not be told their returns are
              fully scheduled. */}
          {enriched.length === 0 ? (
            <div className="sched-rpt-muted" style={{ padding: '18px 2px' }}>
              {data.scope === 'firm'
                ? <>No return jobs are in scope for {data.returnYear}. If that looks wrong, check
                    the return year above &mdash; the automatic choice for today is {data.autoReturnYear}.</>
                : <>You aren&rsquo;t job manager or job partner on any {data.returnYear} tax return,
                    so there is nothing to show. This report covers tax returns only &mdash; work on
                    other service lines won&rsquo;t appear here, whoever leads it.</>}
            </div>
          ) : (
            <>
              <div style={{ fontWeight: 600, fontSize: 13, color: BRAND.deepTeal, margin: '14px 0 4px' }}
                title="Jobs that were also done last year, so last year's prep hours are the starting point.">
                Prior-year baseline &mdash; {mainRows.length} job{mainRows.length === 1 ? '' : 's'}
                {mainHours > 0 && <span className="sched-rpt-muted-sm">{'  '}({mainHours} h)</span>}
              </div>
              <GapTable rows={mainRows} sort={sortMain} showBaselineSource={false} onPick={openChooser}
                emptyText={minGap > 0
                  ? `No job with a prior-year baseline has more than ${minGap} h outstanding. Clear the minimum to see smaller gaps.`
                  : 'No jobs with a prior-year baseline are missing hours. Nothing to schedule.'} />

              {noBaseRows.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 13, color: BRAND.deepTeal, margin: '22px 0 4px' }}
                    title="Jobs with no prior year to compare against - new clients, new entities, first-year returns.">
                    No prior-year baseline &mdash; {noBaseRows.length} job{noBaseRows.length === 1 ? '' : 's'}
                  </div>
                  <div className="sched-rpt-muted-sm" style={{ marginBottom: 6 }}>
                    First-year work, so there is no prior year to compare against. Where the job has a
                    prep <em>budget</em> for this year ({stats.fallbackBaseline} of these), that is used as
                    the starting point instead. Where it has neither ({stats.noBaseline}), the hours are
                    left <strong>blank rather than zero</strong> &mdash; nothing is known, which is not
                    the same as nothing to do.
                  </div>
                  <GapTable rows={noBaseRows} sort={sortNoBase} showBaselineSource onPick={openChooser}
                    emptyText="None." />
                </>
              )}
            </>
          )}
        </>
      )}

      {chooser && (
        <ScheduleChooserModal
          job={chooser.job}
          initial={chooser.initial}
          meStaffId={meStaffId}
          meName={meName}
          staff={staff}
          variant="myjobs"
          origin="report"
          onClose={() => setChooser(null)}
        />
      )}
    </div>
  );
}
